import { GroupMemberRole, GroupMemberStatus } from '@/models/GroupMember'
import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'

import config from '@/config'
import { MemberStatus } from '@/models/Groups'
import Users from '@/models/Users'
import { PaginationParams } from '@/types/PaginationParams'
import BaseService from './BaseService'
import { requests } from './HTTPService'
import {
  AddUserToGroupRequestType,
  SubmitMembershipAnswersRequestType,
} from './types/UsersService.type'

const http = requests(config?.COMMUNITIES_SERVER_URL)
export default class UsersService extends BaseService<Users> {
  readonly groupId: string

  model(): typeof Users {
    return Users
  }

  constructor(locationId: string, groupId: string) {
    super(locationId)
    this.groupId = groupId
    this.basePath = `/${locationId}/groups/${groupId}/users`
  }

  async getGroupMembersList(
    memberRole?: GroupMemberRole[],
    memberStatus?: GroupMemberStatus
  ): Promise<any> {
    const response = await http.get(`${this.endpoint}/list`, {
      params: {
        memberRole: memberRole?.toString(),
        memberStatus,
      },
    })
    return response.data
  }

  async getGroupMemberRequestCount(): Promise<any> {
    const response = await http.get(`${this.endpoint}/request/count`)
    return response.data
  }

  async getGroupMembersDetails(
    memberRole?: GroupMemberRole,
    memberStatus?: GroupMemberStatus,
    searchText?: string,
    PaginationParams?: PaginationParams,
    showEmail?: boolean
  ): Promise<any> {
    const response = await http.get(`${this.endpoint}/details`, {
      params: {
        memberRole,
        memberStatus,
        searchText,
        ...PaginationParams,
        showEmail,
      },
    })
    return response.data
  }

  async getGroupMembersDetailsForChannelSuggestions(
    channelId: string,
    searchText?: string,
    memberRole?: GroupMemberRole,
    PaginationParams?: PaginationParams
  ): Promise<any> {
    const response = await http.get(
      `${this.endpoint}/channel-suggestions/${channelId}`,
      {
        params: {
          memberRole,
          searchText,
          ...PaginationParams,
        },
      }
    )
    return response.data
  }

  async getUserDataBySlug(slug: string): Promise<any> {
    return http
      .get(`${this.endpoint}`, { params: { slug: slug } })
      .then(res => res.data)
  }

  async getUserDataBycontactId(contactId: string): Promise<any> {
    return http.get(`${this.endpoint}/${contactId}`).then(res => res.data)
  }

  async joinGroup(
    role: string,
    contactId: string,
    status: MemberStatus,
    orderId: string
  ): Promise<any> {
    const response = await http.post(`${this.endpoint}`, {
      contactId,
      role,
      status,
      orderId,
    })
    trackCustomGAEvents(GAEventTypes.GROUP_JOINED, {
      locationId: this.locationId,
    })
    return response.data
  }

  async submitMembershipAnswer(
    payload: SubmitMembershipAnswersRequestType
  ): Promise<any> {
    const response = await http.post(
      `${this.endpoint}/joining-questionnaire`,
      payload
    )
    return response.data
  }

  async updateUserGroup(payload: AddUserToGroupRequestType): Promise<any> {
    const { contactId } = payload
    const url = `${this.endpoint}/${contactId}`
    const response = await http.patch(url, payload)
    return response.data
  }

  async removeUser(contactId: string): Promise<any> {
    const response = await http.delete(`${this.endpoint}/${contactId}`)
    return response.data
  }

  async getUserGroups(contactId: string): Promise<any> {
    const response = await http.get(`${this.endpoint}/${contactId}`)
    return response.data
  }
}
